import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useCustomerStore } from './customer'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestore } from '@/firebase'

import createLogger from '@/utils/logger'
import {
  brandingSchema,
  PaymentMethodEnum,
  SIGNUP_TRIAL_DURATION_DAYS,
  type Branding,
} from '@gh/common/models'
// import { useStripeCouponsStore } from './stripeCoupons'

const logger = createLogger('stores/branding.ts')

export const useBrandingStore = defineStore('branding', () => {
  const customerStore = useCustomerStore()
  // const stripeCouponsStore = useStripeCouponsStore()
  const customer = computed(() => customerStore.customer)
  const branding = ref<Branding | null>(null)
  let unsubscribeBrandingSnapshot: (() => void) | null = null

  const promoCode = ref<string | null>(null)

  const isLoadingBranding = ref(true)
  const isLoading = computed(() => isLoadingBranding.value || customerStore.isLoading)

  watch(
    customer,
    (newVal) => {
      if (newVal) {
        isLoadingBranding.value = true
        retrieveBrandingFromFirestore()
      } else {
        if (unsubscribeBrandingSnapshot) {
          unsubscribeBrandingSnapshot()
        }
        branding.value = null
      }
    },
    { immediate: true },
  )

  async function retrieveBrandingFromFirestore() {
    if (unsubscribeBrandingSnapshot) {
      unsubscribeBrandingSnapshot()
    }
    const id = customer.value?.referralProgramOrBrandingId
    if (!id) {
      isLoadingBranding.value = false
      return
    }
    unsubscribeBrandingSnapshot = onSnapshot(doc(firestore, 'brandings', id), (docSnap) => {
      if (docSnap.exists()) {
        const { success, data, error } = brandingSchema.safeParse({
          id: docSnap.id,
          ...docSnap.data(),
        })
        if (!success) {
          logger.error('Error parsing branding data', { data, error, snap: docSnap.data() })
          isLoadingBranding.value = false
          return
        }
        branding.value = data
      }
    })
  }

  watch(
    branding,
    (newBranding) => {
      if (newBranding) {
        const paymentMethod = newBranding.paymentMethod
        const paymentConfig = newBranding.paymentMethodConfigs[paymentMethod]

        // Check if the payment config has a promo code
        if (paymentConfig && 'promoCode' in paymentConfig && paymentConfig.promoCode) {
          promoCode.value = paymentConfig.promoCode
        }

        isLoadingBranding.value = false
      }
    },
    { immediate: true },
  )

  function signOut() {
    if (unsubscribeBrandingSnapshot) {
      unsubscribeBrandingSnapshot()
    }
    branding.value = null
    isLoadingBranding.value = true
  }

  const defaultHearAboutUs = computed(() => branding.value?.programType === 'individual')

  const trialDays = computed(() => {
    if (branding.value?.paymentMethod === PaymentMethodEnum.Sponsored) {
      return 0
    }
    const configuredTrialDays = branding.value?.paymentMethodConfigs.default?.trialDays
    if (
      typeof configuredTrialDays === 'number' &&
      Number.isInteger(configuredTrialDays) &&
      configuredTrialDays >= 0 &&
      configuredTrialDays <= 30
    ) {
      return configuredTrialDays
    }
    return SIGNUP_TRIAL_DURATION_DAYS
  })

  return {
    branding,
    signOut,
    isLoading,
    promoCode,
    defaultHearAboutUs,
    trialDays,
  }
})
